import React, { useState } from 'react';
import { FaChevronDown, FaChevronRight, FaClipboardList, FaEye, FaInfoCircle, FaLayerGroup, FaListUl, FaGripVertical, FaMagic, FaPencilAlt, FaPlus, FaSearch, FaTimes, FaTrash, FaUndo } from 'react-icons/fa';
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter, TraversalOrder } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AgrupamentoBoard from './AgrupamentoBoard';
import PillObservacao from './PillObservacao';
import { FinTextarea } from './FinField';
import { baseDoVinculo, chaveOrigem, dataDoVinculo, opcaoTemLado, pontasDe, rotaDoTrecho, vinculoDe, pedeIda, pedeRetorno } from '../util/malhaAereo';
import '../css/financeiro-parcela-pagar.css';

const BRAND_NAVY   = '#1f3c88';
const BRAND_BLUE   = '#2d7ff9';
const BRAND_SKY    = '#eef6ff';
const BRAND_BORDER = '#dbe8f8';

// Paleta de status (igual ao toast-theme).
export const COR_STATUS = { valida: '#10b981', divergente: '#f59e0b', faltando: '#ef4444', orfa: '#ef4444' };
// "Trechos com opção" leva o índigo da própria Origem, e não o verde de Completa: ele não diz que
// algo está certo, diz de que NÍVEL veio a cobertura — a opção está num trecho, não nela.
const COR_ESTADO = { completa: '#10b981', divergente: '#f59e0b', faltando: '#ef4444', semOpcao: '#6b7280', trechosComOpcao: '#6366f1' };
const ROTULO_ESTADO = { completa: 'Completa', divergente: 'Divergente', faltando: 'Faltando', semOpcao: 'Sem opção', trechosComOpcao: 'Trecho com opção' };
const PLURAL_ESTADO = { completa: 'Completas', divergente: 'Divergentes', faltando: 'Faltando', semOpcao: 'Sem opção', trechosComOpcao: 'Trechos com opção' };

// Padrão dos badges de contagem: "<n> <Nome>" — plural só quando n > 1 (Faltando é
// invariável); contagem 0 não renderiza badge.
const rotuloContagem = (n, singular, plural) => `${n} ${n > 1 ? plural : singular}`;

// Badges de um trecho no nível de OPÇÃO (consistente com o topo): uma opção
// pode aparecer em Divergente E Faltando ao mesmo tempo, então o trecho pode
// mostrar os dois. Sem opção alguma → badge "Sem opção" (Faltando é SÓ campo
// faltando numa opção relacionada, nunca ausência de opções).
const badgesTrecho = (r, daOrigem = null) => {
    // A ORIGEM que tem trecho é outro caso: quem responde pelo pedido dela são eles. Havendo opção
    // em algum trecho dela, o badge diz em quantos e o "Sem opção" dela some. Sem opção em trecho
    // nenhum, vale a regra normal: sem opção nela mesma, "Sem opção".
    if (daOrigem && daOrigem.trechos > 0 && daOrigem.comOpcao > 0) {
        return [{ estado: 'trechosComOpcao', n: daOrigem.comOpcao }];
    }
    if (r.total === 0) return [{ estado: 'semOpcao', n: 0 }];
    const out = [];
    if (r.completas)   out.push({ estado: 'completa',   n: r.completas });
    if (r.divergentes) out.push({ estado: 'divergente', n: r.divergentes });
    if (r.faltando)    out.push({ estado: 'faltando',   n: r.faltando });
    return out;
};
const rotuloBadge = (b) => (b.n > 0
    ? rotuloContagem(b.n, ROTULO_ESTADO[b.estado], PLURAL_ESTADO[b.estado])
    : ROTULO_ESTADO[b.estado]); // trecho sem opção alguma → "Sem opção" seco (sem número)

// Renderiza a fila de pills de estado de um trecho (1+).
function PillsEstado({ r, daOrigem = null }) {
    return badgesTrecho(r, daOrigem).map((b, i) => (
        <Pill key={i} cor={COR_ESTADO[b.estado]}>{rotuloBadge(b)}</Pill>
    ));
}

const ORFAS_ID = '__orfas__';
const primeiraLinha = (v) => String(v ?? '').split('\n')[0].trim();
const ultimaLinha = (v) => {
    const linhas = String(v ?? '').split('\n');
    return linhas[linhas.length - 1].trim();
};

function Pill({ cor, children }) {
    return (
        <span className="fw-bold rounded-pill text-nowrap"
            style={{ background: cor, color: '#fff', fontSize: 11, padding: '2px 9px', letterSpacing: '0.03em' }}>
            {children}
        </span>
    );
}

// Rótulo do chip de opção — SÓ a IDA, e em conexões ('\n') só os ENDPOINTS: nº ·
// 1ª origem <seta> último destino | 1ª cia + voo | 1ª data + hora. Nada mais (sem
// retorno, sem total). A seta indica ida-e-volta: com retorno "⇄", só ida "➝".
// Ex.: "1 · SAO ⇄ CWB | GOL 1104 | 28/05 06:40".
// A opção só de volta não tem ida para mostrar, e saía "? ⇄ ?": ela mostra a volta, com o destino
// na frente e a seta para trás ("1 · FOR ← THE | LATAM 3492 | 11/09"), o lugar do passageiro no
// mesmo canto das outras opções.
const rotuloOpcao = (op) => {
    const lado = opcaoTemLado(op, 'ida') || !opcaoTemLado(op, 'ret') ? 'ida' : 'ret';
    const origem  = primeiraLinha(op[`${lado}OrigemIata`]) || primeiraLinha(op[`${lado}OrigemAeroporto`]) || '?';
    const destino = ultimaLinha(op[`${lado}DestinoIata`]) || ultimaLinha(op[`${lado}DestinoAeroporto`]) || '?';
    const cia  = primeiraLinha(op[`${lado}Cia`]);
    const voo  = primeiraLinha(op[`${lado}Voo`]);
    const data = primeiraLinha(op[`${lado}Data`]).split('/').slice(0, 2).join('/'); // sem ano
    const hora = primeiraLinha(op[`${lado}Saida`]);
    const temRetorno = !!(primeiraLinha(op.retOrigemIata) || primeiraLinha(op.retOrigemAeroporto)
        || primeiraLinha(op.retDestinoIata) || primeiraLinha(op.retDestinoAeroporto));

    const trecho = [lado === 'ret' ? `${destino} ← ${origem}` : `${origem} ${temRetorno ? '⇄' : '➝'} ${destino}`];
    if (cia || voo) trecho.push(`${cia} ${voo}`.trim());
    const horario = [data, hora].filter(Boolean).join(' ');
    if (horario) trecho.push(horario);
    return `${String(op.opcao || '-')}  ·  ${trecho.join(' | ')}`;
};

// Rótulo do trecho: TODOS os campos do trecho — rota, datas (ida → retorno,
// só as preenchidas) e passageiros. Ex.: "São Paulo → Curitiba · 28/05/2026 → 02/06/2026 · 3 pax".
// "14:30:00" → "14:30"; vazio ou meia-noite (default de "sem hora") → ''.
const horaCurta = (h) => {
    const t = String(h ?? '').trim();
    if (!t || t.startsWith('00:00')) return '';
    const m = t.match(/^(\d{1,2}):(\d{2})/);
    return m ? `${m[1].padStart(2, '0')}:${m[2]}` : '';
};

// Data de um voo com a hora: "18/09/2026 09:00 – 11:00". O traço é o que liga as duas pontas, então
// só aparece com as duas — com uma hora só ele ficaria pendurado sem nada do outro lado.
// Sozinha, a chegada leva "até": ela é um teto, e sem a palavra a hora seria lida como a de sair.
// A saída sozinha vai crua, na posição em que já se espera a hora de partir.
const dataComHoras = (data, saida, chegada) => {
    const d = String(data ?? '').trim();
    const hs = horaCurta(saida);
    const hc = horaCurta(chegada);
    const faixa = hs && hc ? `${hs} – ${hc}` : (hs || (hc ? `até ${hc}` : ''));
    return [d, faixa].filter(Boolean).join(' ');
};

// O rótulo mostra só o que aquele vínculo tem DE DIFERENTE do pedido: datas herdadas se repetiriam
// em toda linha e poluiriam a leitura sem dizer nada. A rota sai no mesmo formato do cabeçalho do
// trecho na tela de solicitação, "São Paulo (Congonhas) ⇄ Salvador" (`rotaDoTrecho`, a regra única
// dela): o lugar de cima com o detalhe entre parênteses, e a volta só quando não é o espelho da ida.
const rotuloTrecho = (s, pedidoDoTrecho) => {
    const pedido = pedidoDoTrecho ?? {};
    const ida = pedeIda(s), retorno = pedeRetorno(s);
    const partes = [rotaDoTrecho(pontasDe(s), { ida, retorno }) || (ida && retorno ? '? ⇄ ?' : '? ➝ ?')];
    if (!ida || !retorno) partes.push(ida ? 'só ida' : 'só retorno');
    // Data só quando é outra que a do pedido; hora sempre, que hora é do trecho.
    const igual = (a, b) => String(a ?? '').trim() === String(b ?? '').trim();
    // Sem escrever "ida" e "retorno": a rota logo antes já diz qual é qual, e a ordem também.
    const voo = (data, doPedido, de, ate) => dataComHoras(igual(data, doPedido) ? '' : data, de, ate);
    const voos = [
        ida && voo(dataDoVinculo(s, 'ida'), pedido.dataIda, s.horaSaidaIdaDe, s.horaChegadaIdaAte),
        retorno && voo(dataDoVinculo(s, 'ret'), pedido.dataRetorno, s.horaSaidaRetornoDe, s.horaChegadaRetornoAte),
    ].filter(Boolean);
    if (voos.length) partes.push(voos.join(' ⇄ '));
    if (s.quantidadePassageiros != null) partes.push(`${s.quantidadePassageiros} pax`);
    return partes.join(' · ');
};

// Quantos trechos pendem de cada origem, e quantos deles JÁ TÊM opção. O primeiro número vai no
// badge de tipo ("Origem · 2 trechos") e tira a origem da contagem de "Sem opção", que passa a ser
// dos trechos dela; o segundo vira o badge de estado dela, porque a opção que responde pelo pedido
// pode estar num trecho e a origem parecia descoberta. A origem em si nunca sai do painel: trecho
// muda a qualquer momento, origem não.
const trechosPorOrigem = (vinculos, resumoTrechos) => {
    const temOpcao = new Set((resumoTrechos ?? []).filter(r => r.total > 0).map(r => String(r.trecho.id)));
    const mapa = new Map();
    (vinculos ?? []).forEach(s => {
        const pai = String(s?.origem_id ?? '');
        if (!pai || baseDoVinculo(s) === 'origem') return;
        const conta = mapa.get(pai) ?? { trechos: 0, comOpcao: 0 };
        conta.trechos += 1;
        if (temOpcao.has(String(s.id))) conta.comOpcao += 1;
        mapa.set(pai, conta);
    });
    return mapa;
};
// Só a origem tem o quadro dos trechos dela; para trecho e grupo é null, e os badges seguem a
// regra normal, de opção.
const daOrigemDe = (s, mapa) => (baseDoVinculo(s) === 'origem'
    ? (mapa?.get(String(s.id)) ?? { trechos: 0, comOpcao: 0 })
    : null);
const contagemDaOrigem = (s, mapa) => daOrigemDe(s, mapa)?.trechos ?? 0;

// Trecho, origem e grupo se editam: a edição do trecho e da origem vale só nesta malha.
const podeEditar = (s) => ['trecho', 'origem', 'grupo'].includes(baseDoVinculo(s));

// Monta os grupos (containers) para o board a partir das opções e do status calculado.
function construirGrupos(trechos, opcoes, statusPorOpcao, resumoTrechos, onEditar, pedido, porOrigem, onDesfazerVinculo) {
    const porTrecho = {};
    trechos.forEach(s => { porTrecho[String(s.id)] = []; });
    const orfas = [];

    (opcoes ?? []).forEach((op, i) => {
        const item = {
            id: op._id ?? `op-${i}`,
            conteudo: rotuloOpcao(op),
            cor: COR_STATUS[statusPorOpcao[i]] || '#cbd5e1',
            // O que a lupa procura na opção: o rótulo e as cidades, aeroportos, companhia e voo dos dois lados.
            busca: chaveOrigem([rotuloOpcao(op), ...['ida', 'ret'].flatMap(l => ['OrigemCidade', 'OrigemIata', 'OrigemAeroporto',
                'DestinoCidade', 'DestinoIata', 'DestinoAeroporto', 'Cia', 'Voo'].map(c => op[l + c]))].join(' ')),
        };
        const sid = vinculoDe(op);
        (sid && porTrecho[sid]) ? porTrecho[sid].push(item) : orfas.push(item);
    });

    const estadoDe = {};
    (resumoTrechos ?? []).forEach(r => { estadoDe[String(r.trecho.id)] = r; });

    const grupos = trechos.map(s => {
        const r = estadoDe[String(s.id)];
        return {
            id: String(s.id),
            label: rotuloTrecho(s, pedido),
            // O que a lupa do board procura: a origem e o destino, da ida e da volta, e o estado dos badges
            // (Divergente, Faltando, Sem opção...).
            busca: chaveOrigem([
                ...Object.values(pontasDe(s)).map(p => p.valor),
                ...(r ? badgesTrecho(r, daOrigemDe(s, porOrigem)).map(b => ROTULO_ESTADO[b.estado]) : []),
            ].join(' ')),
            // Mesmo enquadramento, mesma ação e mesma observação do modo lista — só o board não
            // tinha. A observação vinha compacta, só o (i), porque a coluna do board era estreita;
            // agora ela ocupa a linha inteira e cabe o texto, como na lista.
            tipoBadge: (
                <span className="d-inline-flex align-items-center gap-1" style={{ minWidth: 0 }}>
                    <PillTipo tipo={s.tipo} base={baseDoVinculo(s)} trechos={contagemDaOrigem(s, porOrigem)} />
                    <PillObservacao texto={s.observacao} />
                </span>
            ),
            badge: r ? <span className="d-flex align-items-center gap-1"><PillsEstado r={r} daOrigem={daOrigemDe(s, porOrigem)} /></span> : null,
            acao: podeEditar(s) ? (
                <span className="d-inline-flex align-items-center gap-1">
                    {onEditar && <BotaoEditar onClick={() => onEditar(s)} />}
                    <BotaoDesfazer vinculo={s} onRemover={onDesfazerVinculo} />
                </span>
            ) : null,
            itens: porTrecho[String(s.id)],
        };
    });
    grupos.push({
        id: ORFAS_ID,
        label: 'Órfãs / Não relacionadas',
        badge: orfas.length ? <Pill cor={COR_STATUS.orfa}>{rotuloContagem(orfas.length, 'Órfã', 'Órfãs')}</Pill> : null,
        // A única que nasce aberta, e só tendo órfã: é dela que os itens saem. Fechada, a tela
        // abriria sem nada para arrastar e o primeiro clique seria sempre abri-la. Vazia, ela é só
        // um cabeçalho dizendo que não sobrou nada, e aí fica recolhida como as outras.
        abertoInicial: orfas.length > 0,
        itens: orfas,
    });
    return grupos;
}

// Enquadramento do vínculo. Origem sem trecho, trecho, edição do Aéreo e grupo do aéreo são
// o MESMO elemento na tela — só este badge distingue.
const ROTULO_TIPO = { origem: 'Origem', trecho: 'Trecho', editada: 'Trecho editado', grupo: 'Grupo' };
// Mesma escala (Tailwind 500) do COR_STATUS/COR_ESTADO acima — os badges ficam
// lado a lado com os chips de estado, e tom mais escuro aqui destoa na hora.
const COR_TIPO    = { origem: '#6366f1', trecho: '#64748b', editada: '#8b5cf6', grupo: '#06b6d4' };

// Tipo é categoria, não estado — então contornado, nunca sólido. Sólido fica
// reservado aos badges de estado, que carregam valência (verde/âmbar/vermelho);
// com o mesmo tratamento nos dois, o tipo era lido como mais um status, e o cinza
// de "Trecho" ficava indistinguível do cinza de "Sem opção".
// Mesma receita do .fin-pp__tag: fundo e borda derivados da própria cor.
// padding 1px (e não 2px) compensa a borda, mantendo a altura dos pills de estado.
const PillTipo = ({ tipo, base = null, trechos = 0 }) => {
    const cor = COR_TIPO[tipo] ?? COR_TIPO.trecho;
    // A origem com trechos diz quantos são: ela continua valendo como vínculo, e o número
    // explica por que ela não conta como "Sem opção". A edição diz o que editou: o trecho ou a origem.
    const rotulo = tipo === 'editada' && base === 'origem' ? 'Origem editada' : (ROTULO_TIPO[tipo] ?? ROTULO_TIPO.trecho);
    return (
        <span className="fw-bold rounded-pill text-nowrap"
            title={trechos > 0 ? 'A origem segue valendo; os trechos dela é que detalham o pedido.' : undefined}
            style={{ background: `${cor}14`, color: cor, border: `1px solid ${cor}55`, fontSize: 11, padding: '1px 9px', letterSpacing: '0.03em' }}>
            {trechos > 0 ? `${rotulo} · ${rotuloContagem(trechos, 'trecho', 'trechos')}` : rotulo}
        </span>
    );
};

// Data da solicitação no resumo do cabeçalho recolhido: "18/09/2026" vira "18/09/26". Ano com dois
// dígitos, e não só dia e mês: ida em dezembro e volta em janeiro ficariam ambíguas.
const dataCurta = (d) => {
    const t = String(d ?? '').trim();
    const m = t.match(/^(\d{2})\/(\d{2})\/\d{2}(\d{2})$/);
    return m ? `${m[1]}/${m[2]}/${m[3]}` : t;
};

// Rótulo e valor dos campos fixos da solicitação, no padrão da faixa de dados do cabeçalho do evento.
const ROTULO_CAMPO = { color: '#7b8ba7', fontSize: 11, letterSpacing: '0.05em', fontWeight: 600, textTransform: 'uppercase' };
const VALOR_CAMPO = { color: BRAND_NAVY, fontSize: 14, fontWeight: 600, marginTop: 2 };

// Lápis de edição do vínculo, igual nas duas visualizações. stopPropagation no
// pointerDown: no modo lista a linha inteira é handle de arrasto, e sem isto o
// clique no botão viraria drag.
const BotaoAcao = ({ onClick, title, cor = BRAND_NAVY, borda = BRAND_BORDER, children }) => (
    <button type="button"
        onPointerDown={e => e.stopPropagation()}
        onClick={e => { e.stopPropagation(); onClick(); }}
        title={title}
        className="btn btn-sm d-inline-flex align-items-center justify-content-center"
        style={{ background: '#fff', border: `1px solid ${borda}`, color: cor, borderRadius: 8, padding: '2px 7px', lineHeight: 1 }}>
        {children}
    </button>
);
const BotaoEditar = ({ onClick }) => (
    <BotaoAcao onClick={onClick} title="Editar valores"><FaPencilAlt size={11} /></BotaoAcao>
);

// A ação destrutiva do vínculo, ao lado do lápis — a mesma que o modal oferece no rodapé, e aqui
// sem abrir o modal para isso. Só onde há o que desfazer: lixeira apaga o grupo do Aéreo, seta
// anti-horária devolve o trecho aos valores da solicitação. Vermelha nas duas: as duas perdem o
// que foi digitado nesta malha.
const BotaoDesfazer = ({ vinculo, onRemover }) => {
    const ehGrupo = vinculo.tipo === 'grupo';
    if (!onRemover || !(ehGrupo || vinculo.tipo === 'editada')) return null;
    return (
        <BotaoAcao onClick={() => onRemover(vinculo)} cor={COR_STATUS.faltando} borda={`${COR_STATUS.faltando}55`}
            title={ehGrupo ? 'Excluir grupo' : 'Reverter para os valores da solicitação'}>
            {ehGrupo ? <FaTrash size={11} /> : <FaUndo size={11} />}
        </BotaoAcao>
    );
};

// Linha de trecho no modo lista — a LINHA INTEIRA é arrastável p/ reordenar
// (não só o ícone). O grip fica só como dica visual. Dois cliques focam na malha as opções do
// trecho. Dois, e não um: a linha é alça de arraste, e pegar e soltar sem mover já é um clique.
function LinhaTrecho({ r, arrastavel, onEditar, onFocar, pedido, porOrigem, onDesfazerVinculo }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
        useSortable({ id: String(r.trecho.id), disabled: !arrastavel });
    const style = {
        transform: CSS.Transform.toString(transform), transition,
        opacity: isDragging ? 0.5 : 1, border: `1px solid ${BRAND_SKY}`, borderRadius: 12,
        background: '#fff', cursor: arrastavel ? (isDragging ? 'grabbing' : 'grab') : (onFocar ? 'pointer' : 'default'),
        // Sem isto, os dois cliques selecionam a palavra embaixo do ponteiro.
        ...(onFocar ? { userSelect: 'none' } : {}),
    };
    // listeners/attributes na linha toda → arrasta clicando em qualquer ponto.
    const arrasto = arrastavel ? { ...attributes, ...listeners } : {};
    const dica = [onFocar && 'Clique duas vezes para ver na malha', arrastavel && 'Arraste para reordenar']
        .filter(Boolean).join('. ') || undefined;
    return (
        <div ref={setNodeRef} style={style} className="d-flex align-items-center gap-3 px-3 py-2" {...arrasto}
            onDoubleClick={onFocar ? () => onFocar(r.trecho) : undefined} title={dica}>
            {arrastavel && <FaGripVertical size={13} style={{ color: '#9fb3d4', flexShrink: 0 }} />}
            <span className="fw-semibold text-truncate" style={{ color: BRAND_NAVY, fontSize: 14, minWidth: 0 }}>
                {rotuloTrecho(r.trecho, pedido)}
            </span>
            {/* Enquadramento do vínculo fica colado na identificação e longe dos
                badges de estado: diz o que a linha É, não como ela está. */}
            <span style={{ flexShrink: 0 }}><PillTipo tipo={r.trecho.tipo} base={baseDoVinculo(r.trecho)} trechos={contagemDaOrigem(r.trecho, porOrigem)} /></span>
            {String(r.trecho.observacao ?? '').trim() !== '' && (
                <span className="d-flex" style={{ flex: '0 1 auto', minWidth: 0 }}><PillObservacao texto={r.trecho.observacao} /></span>
            )}
            <span className="ms-auto d-flex align-items-center gap-2" style={{ flexShrink: 0 }}>
                {r.total > 0 && <span style={{ color: '#7b8ba7', fontSize: 12 }}>{r.total} {r.total === 1 ? 'opção' : 'opções'}</span>}
                <PillsEstado r={r} daOrigem={daOrigemDe(r.trecho, porOrigem)} />
                {onEditar && podeEditar(r.trecho) && <BotaoEditar onClick={() => onEditar(r.trecho)} />}
                {podeEditar(r.trecho) && <BotaoDesfazer vinculo={r.trecho} onRemover={onDesfazerVinculo} />}
            </span>
        </div>
    );
}

// Lista de trechos; com onReordenar vira sortable (dnd-kit) p/ mudar SÓ a ordem.
function ListaTrechos({ resumoTrechos, onReordenar, onEditar, onFocar, pedido, porOrigem, onDesfazerVinculo }) {
    // Toque: segura 50ms para arrastar (touch-action em custom.css). Mouse: 5px, como antes —
    // com delay, o dnd-kit cancelaria o arraste rápido que passa de 5px antes da espera.
    const sensores = useSensors(useSensor(PointerSensor, {
        activationConstraint: window.matchMedia('(pointer: coarse)').matches
            ? { delay: 50, tolerance: 5 }
            : { distance: 5 },
    }));
    // Sem DragOverlay aqui: a própria linha anda com o ponteiro, e a transformação dela conta como
    // conteúdo da caixa do painel. Passando do fim da lista, ela esticava a área de rolagem e a
    // rolagem automática seguia a linha para o vazio. O recorte também tira da caixa a sobra
    // lateral das linhas em tela estreita: o overflow-x hidden da caixa não impede o dnd-kit de
    // rolá-la de lado. Recorta só enquanto arrasta, o mesmo recurso do Ajustar malha
    // (AjustarOpcoesModal.js:416-420). Fica acima do `if (!onReordenar)`: a ordem dos hooks é a
    // mesma nos dois caminhos.
    const [arrastando, setArrastando] = useState(false);
    const ids = resumoTrechos.map(r => String(r.trecho.id));

    const aoSoltar = (e) => {
        setArrastando(false);
        const { active, over } = e;
        if (!over || String(active.id) === String(over.id)) return;
        const de = ids.indexOf(String(active.id));
        const para = ids.indexOf(String(over.id));
        if (de < 0 || para < 0) return;
        onReordenar(arrayMove(ids, de, para));
    };

    if (!onReordenar) {
        return (
            <div className="d-flex flex-column gap-2">
                {resumoTrechos.map(r => <LinhaTrecho key={r.trecho.id} r={r} arrastavel={false} onEditar={onEditar} onFocar={onFocar} pedido={pedido} porOrigem={porOrigem} onDesfazerVinculo={onDesfazerVinculo} />)}
            </div>
        );
    }

    return (
        <DndContext sensors={sensores} collisionDetection={closestCenter}
            // A caixa do painel antes da página: no padrão (TreeOrder) o dnd-kit tenta do mais externo
            // para o mais interno, e a página rolava no lugar da caixa (a mesma constatação de
            // AgrupamentoBoard.js:164-168). A aceleração é a do board (AgrupamentoBoard.js:176): o
            // ponteiro agora passa da borda da caixa (barra de botões em cima, planilha embaixo), e a
            // velocidade cresce sem teto com a distância; no padrão (10) a lista passava num instante.
            autoScroll={{ order: TraversalOrder.ReversedTreeOrder, acceleration: 2 }}
            onDragStart={() => setArrastando(true)} onDragEnd={aoSoltar} onDragCancel={() => setArrastando(false)}>
            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                <div className="d-flex flex-column gap-2" style={{ overflow: arrastando ? 'hidden' : 'visible' }}>
                    {resumoTrechos.map(r => <LinhaTrecho key={r.trecho.id} r={r} arrastavel={true} onEditar={onEditar} onFocar={onFocar} pedido={pedido} porOrigem={porOrigem} onDesfazerVinculo={onDesfazerVinculo} />)}
                </div>
            </SortableContext>
        </DndContext>
    );
}

/**
 * Painel "Solicitação": um accordion único com badges totais e, expandido, a lista de trechos
 * com badges específicos + o board de agrupamento. O estado aberto/fechado é controlado pela
 * página (liga as cores da tabela).
 *
 * Os campos fixos da solicitação (`pedido`: destino, datas e observação) são só leitura.
 * Recolhido, viram o resumo embaixo do título ("Bahia · 18/09/26 ⇄ 20/09/26") e a observação um
 * (i) com o texto no hover. Aberto, viram a faixa abaixo do cabeçalho; a observação corta numa
 * linha, e o clique na área dela abre o texto inteiro, com as quebras de linha.
 *
 * @param {Function} [onReorganizar] (mapa: { grupoId: [itemId] }) => void — layout do board ao soltar
 *                   (grupoId === '__orfas__' = sem trecho). Sem ela, o board não existe: o botão
 *                   "Agrupamento" some, porque arrastar e remover chip é edição da malha.
 * @param {Function} [onRemover] (itemId) => void          — X no chip: remove a opção da tabela
 * @param {Function} [onDesfazerVinculo] (vinculo) => void — lixeira (grupo) ou reverter (editada)
 * @param {Function} [onReordenar] (idsOrdenados) => void   — nova ordem dos trechos (modo lista)
 * @param {Function} [onVincularOrfas] () => void           — relaciona as órfãs automaticamente (modo board)
 * @param {Function} [onFocar] (ids) => void                 — dois cliques: os `_id` das opções a focar na
 *                   malha, as do vínculo na linha da lista e a própria opção no chip do board
 * @param {string}   [titulo]                                — o nome do painel; a malha sem solicitação (a
 *                   sugestão e o rascunho) o chama de "Grupos". Sem `pedido`, a faixa e o resumo dele não aparecem.
 */
export default function PainelTrechos({ pedido: pedidoRecebido, trechos, opcoes, validacao, aberto, onToggle, onReorganizar, onRemover, onReordenar, onVincularOrfas, onEditar, onAdicionarGrupo, onDesfazerVinculo, onFocar, titulo = 'Solicitação' }) {
    // O pedido chega NULO enquanto a malha carrega, e chegava assim até o rótulo do trecho, que
    // lê `pedido.dataIda`. Valor padrão de parâmetro não cobre isso: ele só vale para `undefined`.
    const pedido = pedidoRecebido ?? {};
    const [modoBoardEscolhido, setModoBoard] = useState(false);
    // A lupa do board: null com o campo fechado; aberto, o texto que filtra por origem, destino e estado.
    const [busca, setBusca] = useState(null);
    // Observação da solicitação: o texto inteiro aberto e o hover da área clicável.
    const [obsAberta, setObsAberta] = useState(false);
    const [obsHover, setObsHover] = useState(false);
    const podeAgrupar = !!onReorganizar;
    const modoBoard = podeAgrupar && modoBoardEscolhido;

    // Sem trecho a seção continua na tela: é por ela que o Aéreo cria o
    // primeiro grupo. Esconder deixaria a ação inalcançável.
    if ((!trechos || trechos.length === 0) && !onAdicionarGrupo) return null;

    const { statusPorOpcao = [], resumoTrechos = [], totais = {} } = validacao || {};
    // Trechos sem NENHUMA opção relacionada (r.total = 0) — badge próprio no topo. Origem COM
    // trecho sai da conta: quem responde pelo pedido dela ali são os trechos, e contá-la de novo
    // dobraria o alarme. Ela continua no painel, com o badge dizendo quantos trechos são.
    const porOrigem = trechosPorOrigem(trechos, resumoTrechos);
    const semOpcao = resumoTrechos.filter(r => r.total === 0 && contagemDaOrigem(r.trecho, porOrigem) === 0).length;
    const grupos = construirGrupos(trechos, opcoes, statusPorOpcao, resumoTrechos, onEditar, pedido, porOrigem, onDesfazerVinculo);
    // Com a lupa, o board mostra o que tem algum dos termos, separados por vírgula, sem acento nem maiúscula: o
    // vínculo cuja origem, destino ou estado tem o termo, com todas as opções; e, nos outros, só as opções que o
    // têm. A coluna das órfãs fica sempre, com as opções dela filtradas: é delas que se arrasta. O escondido não
    // perde nada no arraste: o `onReorganizar` da página mantém como está a opção que não vem no mapa.
    const termos = String(busca ?? '').split(',').map(chaveOrigem).filter(Boolean);
    const filtro = termos.length > 0;
    const casa = (texto) => termos.some(t => (texto ?? '').includes(t));
    const gruposDoBoard = !filtro ? grupos : grupos
        .map(g => (g.id !== ORFAS_ID && casa(g.busca) ? g : { ...g, itens: g.itens.filter(i => casa(i.busca)) }))
        .filter(g => g.id === ORFAS_ID || casa(g.busca) || g.itens.length > 0);
    // As opções que a linha do vínculo foca na malha. A origem responde também pelos trechos dela,
    // como no badge: com a opção num trecho, é lá que ela está.
    const focarVinculo = onFocar ? (s) => {
        const chaves = new Set([String(s.id)]);
        if (baseDoVinculo(s) === 'origem') {
            (trechos ?? []).forEach(t => {
                if (String(t?.origem_id ?? '') === String(s.id) && baseDoVinculo(t) !== 'origem') chaves.add(String(t.id));
            });
        }
        onFocar((opcoes ?? []).filter(op => chaves.has(String(vinculoDe(op) ?? ''))).map(op => String(op._id)));
    } : undefined;

    const obsPedido = String(pedido?.observacao ?? '').trim();
    const resumoPedido = pedidoRecebido
        ? [
            String(pedido.destino ?? '').trim() || '—',
            `${dataCurta(pedido.dataIda) || '—'} ⇄ ${dataCurta(pedido.dataRetorno) || '—'}`,
        ].join(' · ')
        : '';

    return (
        <>
        <div className="bg-white mb-4 overflow-hidden" style={{ border: `1px solid ${BRAND_BORDER}`, borderRadius: 18 }}>
            {/* Header do accordion */}
            <div className="d-flex align-items-center gap-3 px-4 py-3"
                style={{ cursor: 'pointer', background: 'linear-gradient(180deg, #fff 0%, #f7fbff 100%)', borderBottom: aberto ? `1px solid ${BRAND_BORDER}` : 'none' }}
                onClick={() => onToggle && onToggle(!aberto)}>
                <FaClipboardList size={15} style={{ color: BRAND_NAVY, flexShrink: 0 }} />
                <div style={{ minWidth: 0 }}>
                    {/* O (i) fica na linha do título, não no meio do bloco. */}
                    <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
                        <span className="fw-bold text-truncate" style={{ color: BRAND_NAVY, fontSize: 18, lineHeight: 1.25 }}>{titulo}</span>
                        {!aberto && obsPedido && <PillObservacao texto={pedido.observacao} semBadge rotulo="Observações e Premissas de Aéreo" />}
                    </div>
                    {/* Recolhido, o resumo da solicitação; aberto, ela vai para a faixa abaixo. */}
                    {!aberto && resumoPedido && (
                        <div className="text-truncate" style={{ color: '#586478', fontSize: 12.5, marginTop: 1 }}>{resumoPedido}</div>
                    )}
                </div>
                <div className="d-flex align-items-center gap-2 ms-2">
                    {totais.completas > 0 && <Pill cor={COR_ESTADO.completa}>{rotuloContagem(totais.completas, 'Completa', 'Completas')}</Pill>}
                    {totais.divergentes > 0 && <Pill cor={COR_ESTADO.divergente}>{rotuloContagem(totais.divergentes, 'Divergente', 'Divergentes')}</Pill>}
                    {totais.faltando > 0 && <Pill cor={COR_ESTADO.faltando}>{rotuloContagem(totais.faltando, 'Faltando', 'Faltando')}</Pill>}
                    {semOpcao > 0 && <Pill cor="#6b7280">{rotuloContagem(semOpcao, 'Sem opção', 'Sem opção')}</Pill>}
                    {totais.orfas > 0 && <Pill cor={COR_STATUS.orfa}>{rotuloContagem(totais.orfas, 'Órfã', 'Órfãs')}</Pill>}
                </div>
                <span className="ms-auto" style={{ color: '#9fb3d4', fontSize: 13 }}>
                    {aberto ? 'clique para recolher' : 'clique para expandir'}
                </span>
                {aberto
                    ? <FaChevronDown size={13} style={{ color: BRAND_NAVY }} />
                    : <FaChevronRight size={13} style={{ color: BRAND_NAVY }} />}
            </div>

            {/* Aberto: os campos fixos da solicitação numa faixa, só leitura, distribuídos por igual; a
                observação, que pode ser longa, tem teto e corta com reticências. A área inteira dela é
                clicável: o hover destaca o texto e o olho, e o clique abre o texto inteiro. */}
            {aberto && pedidoRecebido && (
                // Espaçamento distribuído, e nenhum campo cresce: é o que mantém o ritmo igual. Sem espaço
                // para todos, a observação quebra para a linha de baixo e fica centralizada nela.
                <div className="d-flex flex-wrap align-items-stretch px-4 py-2"
                    style={{ gap: '8px 28px', justifyContent: 'space-evenly', background: '#f7fbff', borderBottom: `1px solid ${BRAND_BORDER}` }}>
                    {[['Destino', pedido.destino], ['Ida', pedido.dataIda], ['Volta', pedido.dataRetorno]].map(([rotulo, valor]) => (
                        // O bloco inteiro centralizado na própria coluna (justify-self), e não o texto: dá o
                        // mesmo ritmo do espaçamento distribuído, com rótulo e valor alinhados entre si.
                        <div key={rotulo} style={{ flex: '0 0 auto', padding: '4px 0' }}>
                            <div style={ROTULO_CAMPO}>{rotulo}</div>
                            <div className="text-truncate" style={VALOR_CAMPO}>{String(valor ?? '').trim() || '—'}</div>
                        </div>
                    ))}
                    <button type="button" aria-disabled={!obsPedido} tabIndex={obsPedido ? 0 : -1}
                        title={obsPedido.replace(/\s+/g, ' ') || undefined}
                        onClick={() => obsPedido && setObsAberta(true)}
                        onMouseEnter={() => setObsHover(true)} onMouseLeave={() => setObsHover(false)}
                        // Teto em px pelo conforto de leitura: linha longa demais cansa. Cabendo menos, ela
                        // encolhe e corta; não cabendo na linha, quebra para a de baixo.
                        style={{ flex: '0 1 auto', minWidth: 0, maxWidth: 360, textAlign: 'left', border: 0, background: 'transparent', padding: '4px 0', font: 'inherit', cursor: obsPedido ? 'pointer' : 'default' }}>
                        <div className="d-flex align-items-center gap-1" style={ROTULO_CAMPO}>
                            Observação e Premissas
                            {obsPedido && <FaEye size={11} style={{ color: obsHover ? BRAND_BLUE : '#9fb3d4' }} />}
                        </div>
                        {/* No hover, sublinhado colorido: o texto fica igual, e o destaque diz que abre. A
                            cor e a espessura do sublinhado estão na classe, com !important. */}
                        <div className="text-truncate fin-pp__obs-linha" style={{ ...VALOR_CAMPO, fontWeight: 400,
                            textDecorationLine: obsPedido && obsHover ? 'underline' : 'none' }}>
                            {obsPedido.replace(/\s+/g, ' ') || '—'}
                        </div>
                    </button>
                </div>
            )}

            {aberto && (
                <div className="p-4">
                    {/* Alterna entre lista (badges) e board (agrupamento por drag) */}
                    <div className="d-flex justify-content-end gap-2 mb-3">
                        {onAdicionarGrupo && (
                            <button type="button" onClick={onAdicionarGrupo}
                                title="Cria um grupo do Aéreo para organizar cotações que não têm trecho. Não entra na detecção automática."
                                className="btn btn-sm d-inline-flex align-items-center gap-2 fw-semibold me-auto"
                                style={{ background: '#fff', border: `1px solid ${BRAND_BORDER}`, color: BRAND_NAVY, borderRadius: 999, padding: '6px 14px' }}>
                                <FaPlus size={12} />
                                Adicionar grupo
                            </button>
                        )}
                        {/* A lupa abre o campo à esquerda dos botões; fechar limpa o filtro. */}
                        {modoBoard && busca !== null && (
                            <input type="text" autoFocus value={busca} placeholder="Ex.: São Paulo, Rio de Janeiro, divergente"
                                aria-label="Filtrar por origem, destino ou estado"
                                onChange={e => setBusca(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Escape') setBusca(null); }}
                                style={{ width: 300, border: `1px solid ${BRAND_BORDER}`, borderRadius: 999, padding: '5px 14px', fontSize: 13, color: BRAND_NAVY, outline: 'none' }} />
                        )}
                        {modoBoard && (
                            <button type="button" onClick={() => setBusca(b => (b === null ? '' : null))}
                                aria-pressed={busca !== null}
                                title={busca === null ? 'Filtrar os vínculos por origem, destino ou estado' : 'Fechar o filtro'}
                                className="btn btn-sm d-inline-flex align-items-center fw-semibold"
                                style={{ background: busca !== null ? '#eef6ff' : '#fff', border: `1px solid ${BRAND_BORDER}`, color: BRAND_NAVY, borderRadius: 999, padding: '6px 11px' }}>
                                <FaSearch size={12} />
                            </button>
                        )}
                        {/* Ação explícita (não dispara sozinha ao abrir o board): um
                            auto-vínculo automático desfaria o que foi arrastado na mão. */}
                        {modoBoard && onVincularOrfas && totais.orfas > 0 && (
                            <button type="button" onClick={onVincularOrfas}
                                title="Relaciona as opções órfãs ao trecho cuja origem e destino coincidem; sem nenhum assim, ao que coincide numa das pontas. Não altera as já relacionadas."
                                className="btn btn-sm d-inline-flex align-items-center gap-2 fw-semibold"
                                style={{ background: '#fff', border: `1px solid ${BRAND_BORDER}`, color: BRAND_NAVY, borderRadius: 999, padding: '6px 14px' }}>
                                <FaMagic size={12} />
                                Vincular órfãs
                            </button>
                        )}
                        {podeAgrupar && (
                            <button type="button" onClick={() => { setModoBoard(m => !m); setBusca(null); }}
                                className="btn btn-sm d-inline-flex align-items-center gap-2 fw-semibold"
                                style={{ background: modoBoard ? BRAND_NAVY : '#fff', border: `1px solid ${BRAND_BORDER}`, color: modoBoard ? '#fff' : BRAND_NAVY, borderRadius: 999, padding: '6px 14px' }}>
                                {modoBoard ? <FaListUl size={12} /> : <FaLayerGroup size={12} />}
                                {modoBoard ? 'Ver lista' : 'Agrupamento'}
                            </button>
                        )}
                    </div>

                    {/* Teto com rolagem própria: com dezenas de vínculos a lista (ou o board) esticava a página e
                        empurrava a planilha. A altura é a da janela menos o que fica em volta quando o cabeçalho do
                        painel encosta no topo: navbar (50), cabeçalho (~56), faixa do pedido (~65), barra de botões
                        (~47), os dois paddings do p-4 (48), a borda e o rodapé fixo (~63 + folga). Cabe inteira na
                        tela. Piso de 240 para não virar fresta em janela baixa. overflowX hidden: com overflowY auto o
                        eixo x viraria auto, e uma linha de pills que hoje é cortada pelo card ganharia barra lateral.
                        Estilo inline, e não a classe `overflow-auto`, que é `!important` nos dois eixos. */}
                    <div style={{ maxHeight: 'max(240px, calc(100vh - 340px))', overflowY: 'auto', overflowX: 'hidden' }}>
                        {modoBoard ? (<>
                            {filtro && gruposDoBoard.length === 1 && (
                                <p className="mb-2" style={{ color: '#586478', fontSize: 13 }}>
                                    Nenhum vínculo com “{busca.trim()}” na origem, no destino ou no estado.
                                </p>
                            )}
                            <AgrupamentoBoard grupos={gruposDoBoard} onReorganizar={onReorganizar} onRemover={onRemover}
                                onAbrirItem={onFocar ? (id) => onFocar([String(id)]) : undefined} />
                        </>) : (
                            <ListaTrechos resumoTrechos={resumoTrechos} onReordenar={onReordenar} onEditar={onEditar} onFocar={focarVinculo} pedido={pedido} porOrigem={porOrigem} onDesfazerVinculo={onDesfazerVinculo} />
                        )}
                    </div>
                </div>
            )}
        </div>

        {/* Observação da solicitação inteira, com as quebras de linha. Só leitura. */}
        {obsAberta && (
            <div className="modal fade show d-block fin-pp fin-pp__modalbg"
                onClick={(e) => { if (e.target === e.currentTarget) setObsAberta(false); }}>
                <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content fin-pp__modal">
                        <div className="fin-pp__modal-head">
                            <span className="fin-pp__modal-head-ic"><FaInfoCircle size={16} /></span>
                            <h3 className="fin-pp__modal-title">Observações e Premissas de Aéreo
                                <span className="fin-pp__modal-sub">Somente leitura.</span>
                            </h3>
                            <button type="button" className="fin-pp__modal-close" onClick={() => setObsAberta(false)}>
                                <FaTimes size={16} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <FinTextarea readOnly rows={8} value={pedido?.observacao ?? ''}
                                style={{ resize: 'vertical', background: 'var(--fin-sf2)' }} />
                        </div>
                        <div className="fin-pp__modal-foot">
                            <button type="button" className="fin-pp__btn" onClick={() => setObsAberta(false)}>Fechar</button>
                        </div>
                    </div>
                </div>
            </div>
        )}
        </>
    );
}
